// L'éditeur du site du club (D-140, maquettes s3, s4, s5, s8, s9) : #/club/site/editeur[/<page>].
//  * Sur ordinateur (1000 px et plus) : on écrit sur la page même (titre, texte ; gras, italique, lien, couleur du club),
//    une image se change d'un clic (case « J'ai l'accord des personnes visibles »), les blocs se glissent du panneau entre
//    deux blocs (zone de dépôt), les options du bloc choisi, monter / descendre / dupliquer / supprimer, les pages et le
//    menu, l'aperçu téléphone, annuler / rétablir, le brouillon enregistré tout seul (« Brouillon · 3 changements »),
//    « Publier » (ou « Mettre en ligne » : sans l'option, l'écran de l'offre au prorata), l'historique (revenir à une
//    version en fait un brouillon).
//  * Au téléphone : les textes, les photos, l'ordre des blocs et « Publier » ; le reste sur ordinateur.
// Le document est validé par l'app (docProblem) et par la base (_site_doc_check) ; deux personnes à la fois : la seconde
// est prévenue et relit le site.
import {
  BLOCKS, FIXED_TYPES, LIVE_TYPES, PAGE_SLUG, RESERVED_PAGE_SLUGS, SEO_DESC_MAX, SEO_TITLE_MAX, SITE_THEMES, changeNotes, docProblem, euros, groupSlug, isLive,
  linkLabel, newBlock, newId, OPTION_EUROS, OPTION_TTC_CENTS, plainText, prorata, PRORATA, seoDesc, seoTeamDesc, seoTitle, teamPages, themeName, youtubeId,
  type BlockType, type SiteBlock, type SiteDoc, type SitePage, type SiteRoute, type SiteTheme,
} from '../domain/site';
import { slugify } from '../domain/slug';
import { clubLegal } from '../lib/minors-legal';
import { legalMissing } from '../domain/legal';
import { clubPublicPhoto } from '../lib/api';
import {
  liveDataForEditor, mediaUrl, siteNewsList, sitePublish, siteOptionRequest, siteRestore, siteSave, siteState, siteVersionDoc, siteVersions,
  type PublicNews, type SiteData, type SiteState, type SiteVersion,
} from '../lib/site-api';
import type { Ctx } from './context';
import { $, $$, armed, html, mount, openSheet, raw, toast, type Html } from './dom';
import { clubMark } from './theme';
import { artCourt, ico } from './site-art';
import { pickPdf, pickPhoto } from './site-media';
import { hydrateMedia, legalBlocks, pageBlocks, renderSite, siteGroups, teamBlocks, type SiteClub, type SiteView } from './site-render';

/** Le site sur ordinateur ou au téléphone : la largeur de l'écran décide. */
export const wideScreen = () => window.innerWidth >= 1000;

type Tab = 'Blocs' | 'Options' | 'Page' | 'Thème';
interface Core {
  ctx: Ctx; clubId: string; st: SiteState;
  doc: SiteDoc; rev: number; pub: SiteDoc | null;
  data: SiteData | null; club: SiteClub; legal: Awaited<ReturnType<typeof clubLegal>>; news: PublicNews[];
  page: string; sel: string | null;
  undo: string[]; redo: string[];
  saving: boolean; timer: number; pending: Promise<void> | null;
  onChange: () => void;
}

// --- Le cœur : le document, annuler / rétablir, l'enregistrement tout seul ------------------------------------------------
async function loadCore(ctx: Ctx, st: SiteState, page: string | undefined, onChange: () => void): Promise<Core> {
  const clubId = ctx.club!.id;
  const site = st.site!;
  const [data, legal, news] = await Promise.all([
    liveDataForEditor(clubId, st.slug, st.public).catch(() => null),
    clubLegal({ club: clubId }).catch(() => null),
    siteNewsList(clubId).catch(() => []),
  ]);
  const brand = ctx.club!;
  const club: SiteClub = data?.pub.club ?? { name: brand.name, short_name: brand.short_name ?? null, logo: brand.logo_url ?? null, color: brand.primary_color ?? null,
    tagline: null, email: null, address: null, trial: false, timezone: st.tz || 'Europe/Paris' };
  return {
    ctx, clubId, st, doc: JSON.parse(JSON.stringify(site.draft)), rev: site.rev, pub: site.published?.doc ?? null, data, club, legal,
    news: news.map(n => ({ id: n.id, title: n.title, body: n.body, media_id: n.media_id, published_at: n.published_at, teams: n.teams, event: null })),
    page: page && (site.draft.pages.some(p => p.id === page) || page.startsWith('team-')) ? page : site.draft.pages[0]!.id,
    sel: null, undo: [], redo: [], saving: false, timer: 0, pending: null, onChange,
  };
}
function view(c: Core, mode: SiteView['mode'], doc = c.doc): SiteView {
  return { doc, club: c.club, data: c.data, news: c.news, shop: c.st.site?.shop ?? [], director: null, legal: c.legal, color2: c.st.color2, mode,
    link: (p) => `#se:${p}`, membersHref: '#se:app', dataHref: '#se:donnees', partnersHref: '#se:partenaires' };
}
const pageOf = (c: Core) => c.doc.pages.find(p => p.id === c.page) ?? null;
const groupOfPage = (c: Core) => (c.page.startsWith('team-') ? siteGroups({ data: c.data }).find(g => `team-${g.id}` === c.page) ?? null : null);
function routeOf(c: Core, doc = c.doc): SiteRoute | null {
  const g = groupOfPage(c);
  if (g) return { kind: 'team', group: g };
  const p = doc.pages.find(x => x.id === c.page) ?? doc.pages[0]!;
  return { kind: 'page', page: p };
}
const findBlock = (c: Core, id: string | null): { page: SitePage; block: SiteBlock; i: number } | null => {
  if (!id) return null;
  for (const page of c.doc.pages) { const i = page.blocks.findIndex(b => b.id === id); if (i >= 0) return { page, block: page.blocks[i]!, i }; }
  return null;
};
/** Un geste sur le document : annulable, enregistré tout seul. */
function change(c: Core, fn: (d: SiteDoc) => void): boolean {
  const before = JSON.stringify(c.doc);
  const next: SiteDoc = JSON.parse(before);
  fn(next);
  const after = JSON.stringify(next);
  if (after === before) return false;
  const bad = docProblem(next);
  if (bad) { toast(`Impossible : ${bad}.`, 'ko'); return false; }
  c.undo.push(before); if (c.undo.length > 80) c.undo.shift();
  c.redo = [];
  c.doc = next;
  scheduleSave(c);
  c.onChange();
  return true;
}
function undo(c: Core): void { const s = c.undo.pop(); if (!s) return; c.redo.push(JSON.stringify(c.doc)); c.doc = JSON.parse(s); scheduleSave(c); c.onChange(); }
function redo(c: Core): void { const s = c.redo.pop(); if (!s) return; c.undo.push(JSON.stringify(c.doc)); c.doc = JSON.parse(s); scheduleSave(c); c.onChange(); }
function scheduleSave(c: Core): void {
  window.clearTimeout(c.timer);
  c.timer = window.setTimeout(() => void flush(c), 700);
}
/** L'enregistrement du brouillon ; un conflit (une autre personne a publié ou modifié) : le site est relu. */
async function flush(c: Core): Promise<void> {
  window.clearTimeout(c.timer);
  if (c.pending) await c.pending;
  if (c.st.site && JSON.stringify(c.doc) === JSON.stringify(c.st.site.draft)) return;
  c.saving = true; c.onChange();
  c.pending = (async () => {
    try {
      c.rev = await siteSave(c.clubId, c.doc, c.rev);
      if (c.st.site) c.st.site.draft = JSON.parse(JSON.stringify(c.doc));
    } catch (e) {
      if ((e as { code?: string }).code === '40001') {
        toast('Le site a été modifié ailleurs entre-temps : il est relu, tes derniers gestes ne sont pas gardés.', 'ko');
        const st = await siteState(c.clubId).catch(() => null);
        if (st?.site) { c.st = st; c.doc = JSON.parse(JSON.stringify(st.site.draft)); c.rev = st.site.rev; c.pub = st.site.published?.doc ?? null; c.undo = []; c.redo = []; }
      } else toast('Enregistrement impossible pour le moment : vérifie ta connexion.', 'ko');
    } finally { c.saving = false; c.pending = null; c.onChange(); }
  })();
  await c.pending;
}
const notes = (c: Core) => (c.pub ? changeNotes(c.pub, c.doc) : []);
/** Ce que montre un bloc vivant, en quelques mots (l'ordre des blocs au téléphone, maquette s5). */
function liveSummary(c: Core, b: SiteBlock): string {
  const pub = c.data?.pub, n = (k: number, one: string, many: string) => `${k} ${k > 1 ? many : one}`;
  switch (b.type) {
    case 'next': { const m = (pub?.upcoming ?? []).find(x => x.is_home) ?? pub?.upcoming[0]; return m ? `${m.team} – ${m.opponent}` : 'le prochain match'; }
    case 'homes': return n(Math.min((pub?.upcoming ?? []).filter(m => m.is_home).length, Number(b.o.n ?? 4) || 99), 'match', 'matchs');
    case 'away': return n((pub?.upcoming ?? []).filter(m => m.is_home === false).length, 'match', 'matchs');
    case 'results': return n(Math.min((pub?.results ?? []).length, Number(b.o.n ?? 6) || 99), 'résultat', 'résultats');
    case 'agenda': return n((c.data?.agenda ?? []).length, 'rendez-vous', 'rendez-vous');
    case 'partners': return n((pub?.partners ?? []).length, 'partenaire', 'partenaires');
    case 'news': return n(c.news.length + (pub?.news ?? []).length, 'actualité', 'actualités');
    case 'trial': return 'formulaire d’essai';
    case 'teams': return n((pub?.teams ?? []).length, 'groupe', 'groupes');
    default: return String(b.c.t ?? BLOCKS[b.type].label);
  }
}
const online = (c: Core) => c.st.site?.status === 'en_ligne';
function stateLabel(c: Core): { text: string; ok: boolean } {
  if (c.saving) return { text: 'Enregistrement…', ok: false };
  if (!online(c)) return { text: 'Tout est enregistré', ok: true };
  const n = notes(c).length;
  return n ? { text: `Brouillon · ${n} ${n > 1 ? 'changements' : 'changement'}`, ok: false } : { text: 'Publié · rien à publier', ok: true };
}
/** « Publier » (ou « Mettre en ligne ») : l'option active, sinon l'écran de l'offre (s8). */
async function publish(c: Core, phone: boolean): Promise<void> {
  await flush(c);
  if (!c.st.site?.option.active) { openOffer(c, phone); return; }
  const first = !online(c);
  try {
    await sitePublish(c.clubId, first ? ['site mis en ligne', ...notes(c)] : notes(c));
    const st = await siteState(c.clubId);
    if (st?.site) { c.st = st; c.pub = st.site.published?.doc ?? null; c.rev = st.site.rev; }
    toast(first ? 'Le site est en ligne : le bureau est prévenu.' : 'Publié : le site est à jour, le bureau est prévenu.');
    c.onChange();
  } catch (e) {
    const m = String((e as Error).message);
    toast(/adresse/.test(m) ? 'Choisis d’abord l’adresse du club : Club › Page du club › Réglages.' : /option/.test(m) ? 'L’option « Site personnalisable » n’est pas active.' : 'Publication impossible pour le moment.', 'ko');
  }
}

// --- L'écran de l'offre (s8) : le prix (D-142), au prorata de la saison (D-140) ----------------------------------
const MONTHS = ['juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.', 'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin'];
const parisDay = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
function offerBody(c: Core): Html {
  const p = prorata(parisDay());
  const from = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${p.from}T12:00:00`));
  const host = publicHost(c);
  const asked = c.st.site?.option.requested_at;
  return html`
    <p style="margin:0;color:var(--ink-2)">Ton site est prêt. L’option <b>Site personnalisable</b> s’ajoute à l’abonnement du club${PRORATA ? ', au prorata de la saison qui reste' : ' jusqu’au 30 juin'}.</p>
    <div class="se-opt"><div class="lab">Saison ${p.season} <small>du 1er juillet au 30 juin</small></div>
      <div class="se-months">${MONTHS.map((m, i) => html`<span class="${i >= 12 - p.months ? 'on' : ''}"><i></i>${m}</span>`)}</div></div>
    <div class="se-rows">
      <div><span>Option Site personnalisable</span><b>${euros(OPTION_TTC_CENTS)} TTC par an</b></div>
      <div><span>Mise en ligne le ${from} → 30 juin ${p.to.slice(0, 4)}</span><b>${p.months} mois sur 12</b></div>
      <div><span>${PRORATA ? `${euros(OPTION_TTC_CENTS)} × ${p.months} / 12` : `Saison ${p.season}`}</span><span>${euros(p.ht)} HT + ${euros(p.tva)} TVA</span></div>
      <div class="tot"><span>À payer pour cette saison</span><span>${euros(p.ttc)} TTC</span></div></div>
    <ul class="se-list">${['Pages et blocs sans limite, le site à jour tout seul', `L’adresse du club : ${host}`, 'Actualités, historique des versions', 'Mentions légales générées']
      .map(t => html`<li>${ico('check')}<span>${t}</span></li>`)}</ul>
    <p class="hint">Ensuite : ${OPTION_EUROS} TTC par saison, sur le devis de l’abonnement du 1er juillet. Le devis arrive par e-mail ; une fois l’option active, « Mettre en ligne » publie le site aussitôt. Facture ensuite, par carte ou par virement.</p>
    ${asked ? html`<p class="warn">Devis demandé le ${new Date(asked).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} : Arceau te prévient dès que l’option est active.</p>` : ''}`;
}
function openOffer(c: Core, phone: boolean): void {
  const go = async (b: HTMLButtonElement, close: () => void) => {
    b.disabled = true;
    try { await siteOptionRequest(c.clubId); toast('Demande envoyée : Arceau te fait parvenir le devis, puis active l’option. Tu es prévenu.'); close(); }
    catch { b.disabled = false; toast('Demande impossible pour le moment.', 'ko'); }
  };
  if (phone) {
    openSheet('Mettre le site en ligne', html`<div class="form">${offerBody(c)}
      <button type="button" class="btn btn-primary" id="se-quote" data-guide="site-quote">Recevoir le devis →</button>
      <button type="button" class="btn btn-ghost" data-close>Pas maintenant</button></div>`, (sheet, close) => {
      $(sheet, '#se-quote')!.addEventListener('click', e => void go(e.currentTarget as HTMLButtonElement, close));
      $(sheet, '.form [data-close]')!.addEventListener('click', close);
    });
    return;
  }
  const host = document.createElement('div');
  host.className = 'se-modal-bg';
  host.innerHTML = html`<div class="se-modal" role="dialog" aria-modal="true" aria-label="Mettre le site en ligne"><div class="se-modal-h"><h3>Mettre le site en ligne</h3>
    <button type="button" class="sheet-x" data-x aria-label="Fermer">×</button></div>
    <div class="se-modal-b">${offerBody(c)}<div style="display:flex;gap:10px;flex-wrap:wrap"><button type="button" class="btn btn-primary" style="flex:1" id="se-quote" data-guide="site-quote">Recevoir le devis →</button>
    <button type="button" class="btn btn-ghost" data-x>Pas maintenant</button></div></div></div>`.s;
  document.body.append(host);
  const close = () => host.remove();
  host.addEventListener('click', e => { if (e.target === host || (e.target as HTMLElement).closest('[data-x]')) close(); });
  $(host, '#se-quote')!.addEventListener('click', e => void go(e.currentTarget as HTMLButtonElement, close));
}
/** L'adresse du site : le sous-domaine du club (D-032), sinon /c/<adresse>. */
export function publicHost(c: { st: SiteState }): string {
  const base = (import.meta.env.VITE_CLUB_HOST_BASE as string | undefined)?.trim();
  const slug = c.st.slug ?? 'club';
  return base ? `${slug}.${base}` : `${location.host}/c/${slug}`;
}

// --- Le texte enrichi : du contenu modifiable au balisage ------------------------------------------------------------
function toMarkup(el: HTMLElement): string {
  const walk = (n: Node): string => {
    if (n.nodeType === Node.TEXT_NODE) return (n.textContent ?? '').replace(/ /g, ' ');
    if (n.nodeType !== Node.ELEMENT_NODE) return '';
    const e = n as HTMLElement, inner = [...e.childNodes].map(walk).join('');
    const tag = e.tagName.toLowerCase();
    if (tag === 'br') return '\n';
    if (tag === 'b' || tag === 'strong') return inner.trim() ? `**${inner}**` : inner;
    if (tag === 'i' || tag === 'em') return inner.trim() ? `*${inner}*` : inner;
    if (tag === 'mark') return inner.trim() ? `==${inner}==` : inner;
    if (tag === 'a') { const h = e.getAttribute('href') ?? ''; return /^https:\/\//.test(h) ? `[${inner}](${h})` : inner; }
    if (tag === 'p' || tag === 'div') return `\n\n${inner}\n\n`;
    return inner;
  };
  return walk(el).replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n').trim();
}
function setPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let o: Record<string, unknown> | unknown[] = obj;
  keys.slice(0, -1).forEach((k, i) => {
    const next = keys[i + 1]!;
    const cur = (o as Record<string, unknown>)[k];
    if (cur === undefined || cur === null || typeof cur !== 'object') (o as Record<string, unknown>)[k] = /^\d+$/.test(next) ? [] : {};
    o = (o as Record<string, unknown>)[k] as Record<string, unknown>;
  });
  (o as Record<string, unknown>)[keys[keys.length - 1]!] = value;
}
function getPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), obj);
}

// =====================================================================================================================
// Sur ordinateur (s3, s4, s8, s9)
// =====================================================================================================================
export async function renderDesktopEditor(root: HTMLElement, ctx: Ctx, st: SiteState, pageId?: string): Promise<void> {
  let tab: Tab = 'Blocs', device: 'desk' | 'phone' = 'desk', preview = false, history = false, search = '';
  let readonly: { doc: SiteDoc; label: string; id: string; restore: boolean } | null = null;
  let versions: SiteVersion[] | null = null;
  let dragType: BlockType | null = null;
  const c = await loadCore(ctx, st, pageId, () => { drawTop(); drawCanvas(); if (tab !== 'Blocs' || history) drawPanel(); });
  const back = online(c) ? '‹ Site du club' : '‹ Site du club';
  mount(root, html`<section class="screen se-host" data-nav-title="Éditeur du site"><a class="back" hidden href="#/club/site">${back}</a>
    <div class="se-shell" id="se-shell"><header class="se-top" id="se-top"></header><main class="se-canvas" id="se-canvas"></main><aside class="se-panel" id="se-panel"></aside></div></section>`);
  const shell = $(root, '#se-shell')!, top = $(root, '#se-top')!, canvas = $(root, '#se-canvas')!, panel = $(root, '#se-panel')!;

  function drawTop(): void {
    const s = stateLabel(c);
    const g = groupOfPage(c), p = pageOf(c);
    const pageName = g ? `Équipes › ${g.name}` : p?.title ?? 'Accueil';
    mount(top, html`
      <a class="se-quit" href="#/club/site" id="se-quit">‹ Quitter</a>
      <span class="se-site">${clubMark(ctx.club, 'sm')}Site <span class="chip ${online(c) ? 'chip-ok' : 'chip-mute'}">${online(c) ? 'En ligne' : 'Pas en ligne'}</span></span>
      <button type="button" class="se-sel-page" id="se-pages" data-guide="site-pages" aria-haspopup="true">${ico('pages')}<span>${pageName}</span> ▾</button>
      <span class="se-seg" role="group" aria-label="Aperçu"><button type="button" data-dev="desk" aria-pressed="${String(device === 'desk')}" aria-label="Ordinateur">${ico('desk')}<span class="se-lbl">Ordinateur</span></button>
        <button type="button" data-dev="phone" aria-pressed="${String(device === 'phone')}" aria-label="Téléphone" data-guide="site-phone">${ico('phone')}<span class="se-lbl">Téléphone</span></button></span>
      <button type="button" class="se-iconbtn" id="se-undo" title="Annuler" aria-label="Annuler" ${c.undo.length ? '' : 'disabled'}>${ico('undo')}</button>
      <button type="button" class="se-iconbtn" id="se-redo" title="Rétablir" aria-label="Rétablir" ${c.redo.length ? '' : 'disabled'}>${ico('redo')}</button>
      <button type="button" class="se-txtbtn" id="se-hist" aria-pressed="${String(history)}" aria-label="Historique" data-guide="site-history">${ico('hist')}<span class="se-lbl">Historique</span></button>
      <span class="se-grow"></span>
      <span class="se-state ${s.ok ? 'ok' : ''}" data-guide="site-state"><i></i>${s.text}</span>
      <button type="button" class="se-iconbtn" id="se-eye" title="Aperçu" aria-label="Aperçu" aria-pressed="${String(preview)}">${ico('eye')}</button>
      <button type="button" class="btn btn-primary" id="se-pub" data-guide="site-publish" ${online(c) && !notes(c).length && !c.saving ? 'disabled' : ''}>${online(c) ? 'Publier' : 'Mettre en ligne'}</button>`);
  }

  let shownPage = c.page;
  function drawCanvas(): void {
    const doc = readonly?.doc ?? c.doc;
    const v = view(c, readonly || preview ? 'view' : 'edit', doc);
    const scroll = shownPage === c.page ? canvas.scrollTop : 0;
    shownPage = c.page;
    const route = routeOf(c, doc);
    mount(canvas, html`<div class="se-frame ${device === 'phone' ? 'se-phone' : ''}"><div class="se-zoom">${renderSite(v, route)}</div></div>
      ${readonly ? html`<div class="se-banner">${ico('hist')} ${readonly.label} · lecture seule
        ${readonly.restore ? html`<button type="button" class="btn btn-primary" data-restore="${readonly.id}">Revenir à cette version</button>` : ''}<button type="button" class="btn btn-ghost" data-close-ro>Fermer</button></div>` : ''}`);
    canvas.scrollTop = scroll;
    shell.style.gridTemplateColumns = preview ? 'minmax(0, 1fr)' : '';
    panel.hidden = preview;
    hydrateMedia(canvas, id => mediaUrl(id, { club: c.clubId }), id => (c.st.public && c.st.slug ? clubPublicPhoto(c.st.slug, id).then(b => (b ? `data:image/jpeg;base64,${b}` : null)).catch(() => null) : Promise.resolve(null)));
    if (!readonly && !preview) decorate();
  }
  /** Le bloc choisi : son cadre, son étiquette, sa barre (Options, monter, descendre, dupliquer, supprimer). */
  function decorate(): void {
    const el = c.sel ? canvas.querySelector<HTMLElement>(`[data-bid="${c.sel}"]`) : null;
    if (!el) return;
    const f = findBlock(c, c.sel)!;
    el.classList.add('se-sel');
    // Le bandeau du haut (sous l'en-tête du site) : l'étiquette en bas, la barre sous le menu (maquette s3).
    if (f.i === 0 && f.block.type === 'text' && f.block.variant === 'banniere') el.classList.add('se-label-b');
    el.insertAdjacentHTML('beforeend', html`<div class="se-bar" data-guide="site-bar"><button type="button" class="${tab === 'Options' ? '' : 'hl'}" data-bar="opts">${ico('gear')}Options</button><span class="sep"></span>
      <button type="button" data-bar="up" title="Monter" aria-label="Monter" ${f.i === 0 ? 'disabled' : ''}>${ico('up')}</button>
      <button type="button" data-bar="down" title="Descendre" aria-label="Descendre" ${f.i === f.page.blocks.length - 1 ? 'disabled' : ''}>${ico('down')}</button>
      <button type="button" data-bar="dup" title="Dupliquer" aria-label="Dupliquer">${ico('copy')}</button>
      <button type="button" data-bar="del" title="Supprimer" aria-label="Supprimer">${ico('trash')}</button></div>`.s);
  }
  function select(id: string | null, toOptions = false): void {
    c.sel = id;
    if (toOptions && id) tab = 'Options';
    drawCanvas(); drawPanel();
  }

  // --- Le panneau : Blocs, Options, Page, Thème ; l'historique --------------------------------------------------------
  function drawPanel(): void {
    if (history) { void drawHistory(); return; }
    const tabs = html`<nav class="se-tabs">${([['Blocs', 'plus'], ['Options', 'gear'], ['Page', 'doc'], ['Thème', 'palette']] as const).map(([t, i]) =>
      html`<button type="button" data-tab="${t}" aria-pressed="${String(t === tab)}">${ico(i)}${t}</button>`)}</nav>`;
    const body = tab === 'Blocs' ? blocksPanel() : tab === 'Options' ? optionsPanel() : tab === 'Page' ? pagePanel() : themePanel();
    mount(panel, html`${tabs}<div class="se-pbody">${body}</div>`);
    hydrateMedia(panel, id => mediaUrl(id, { club: c.clubId }), () => Promise.resolve(null));
  }
  function blocksPanel(): Html {
    const q = search.trim().toLowerCase();
    const hit = (t: BlockType) => !q || BLOCKS[t].label.toLowerCase().includes(q) || BLOCKS[t].words.includes(q);
    const tile = (t: BlockType) => html`<button type="button" class="se-tile ${isLive(t) ? 'se-lv' : ''}" draggable="true" data-add="${t}" ${t === 'partners' ? html`data-guide="site-tile"` : ''}>
      <span class="se-ti">${ico(BLOCKS[t].icon)}</span><span>${BLOCKS[t].label}</span></button>`;
    const generated = !!groupOfPage(c) || pageOf(c)?.kind === 'legal';
    return html`
      <label class="se-search">${ico('search')}<input type="search" id="se-q" placeholder="Chercher un bloc" value="${search}" aria-label="Chercher un bloc"></label>
      ${generated ? html`<p class="se-note">Cette page est <b>générée</b> : elle se remplit toute seule. Les blocs se posent sur les autres pages.</p>` : ''}
      <div class="se-ph"><h4>Blocs vivants</h4><span class="se-live-dot">à jour tout seuls</span></div>
      <div class="se-tiles" data-guide="site-blocks">${LIVE_TYPES.filter(hit).map(tile)}</div>
      <div class="se-ph"><h4>Blocs fixes</h4><small>tu écris, tu choisis</small></div>
      <div class="se-tiles">${FIXED_TYPES.filter(hit).map(tile)}</div>
      <p class="se-note">Glisse un bloc entre deux blocs de la page, ou touche-le : il arrive sous le bloc choisi.</p>`;
  }

  const seg = (path: string, opts: [unknown, string][], cur: unknown) => html`<span class="se-seg">${opts.map(([v, l]) =>
    html`<button type="button" data-set="${path}" data-val="${JSON.stringify(v)}" aria-pressed="${String(JSON.stringify(cur) === JSON.stringify(v))}">${l}</button>`)}</span>`;
  const tog = (path: string, label: string, sub: string, on: boolean) => html`<div class="se-tog"><span>${label}${sub ? html`<small>${sub}</small>` : ''}</span>
    <button type="button" class="se-sw-tog" role="switch" aria-checked="${String(on)}" aria-label="${label}" data-toggle="${path}" data-on="${String(on)}"></button></div>`;
  const VAR_BARS: Record<string, [number, number, number, number, string][]> = {
    banniere: [[10, 30, 50, 12, 'w'], [10, 50, 36, 8, 'w'], [10, 68, 22, 14, 'o']], image: [[8, 26, 38, 10, 'g'], [8, 44, 30, 8, 'g'], [8, 62, 20, 12, 'c'], [56, 14, 36, 72, 'g']],
    centre: [[22, 26, 56, 12, 'g'], [28, 46, 44, 8, 'g'], [38, 64, 24, 14, 'c']], cartes: [[6, 14, 42, 32, 'g'], [52, 14, 42, 32, 'g'], [6, 54, 42, 32, 'g'], [52, 54, 42, 32, 'g']],
    liste: [[6, 12, 88, 16, 'g'], [6, 34, 88, 16, 'g'], [6, 56, 88, 16, 'g'], [6, 78, 60, 10, 'g']], bandeau: [[4, 22, 28, 56, 'c'], [36, 22, 28, 56, 'g'], [68, 22, 28, 56, 'g']],
  };
  const COL: Record<string, string> = { w: 'rgba(255,255,255,.9)', g: 'color-mix(in srgb, var(--ink) 30%, transparent)', c: 'var(--accent)', o: '#F28C28' };
  const variants = (b: SiteBlock) => (BLOCKS[b.type].variants.length < 2 ? '' : html`<div class="se-opt"><div class="lab">Variante</div><div class="se-variants">${BLOCKS[b.type].variants.map(v =>
    html`<button type="button" class="se-var" data-variant="${v.key}" aria-pressed="${String(b.variant === v.key)}"><i style="${v.key === 'banniere' ? 'background:#1B2436' : ''}">${raw((VAR_BARS[v.key] ?? VAR_BARS.centre!).map(([l, t, w, h, k]) =>
      `<span style="left:${l}%;top:${t}%;width:${w}%;height:${h}%;background:${COL[k]}"></span>`).join(''))}</i>${v.label}</button>`)}</div></div>`);
  const groupChips = (b: SiteBlock) => {
    const all = siteGroups({ data: c.data });
    if (!all.length) return '';
    const pick = (Array.isArray(b.o.groups) ? b.o.groups : []) as string[];
    const on = (id: string) => !pick.length || pick.includes(id);
    return html`<div class="se-opt"><div class="lab">Groupes affichés <small>${pick.length ? `${pick.length} sur ${all.length}` : `les ${all.length}`}</small></div>
      <div class="se-chk">${all.map(g => html`<button type="button" data-group-chip="${g.id}" aria-pressed="${String(on(g.id))}">${g.name}</button>`)}</div></div>`;
  };
  const pageTargets = (cur: string) => html`<option value="trial" ${cur === 'trial' ? 'selected' : ''}>Demande d’essai</option><option value="follow" ${cur === 'follow' ? 'selected' : ''}>☆ Suivre</option>
    ${c.doc.pages.filter(p => p.kind !== 'legal').map(p => html`<option value="page:${p.id}" ${cur === `page:${p.id}` ? 'selected' : ''}>Page ${p.title}</option>`)}
    <option value="url" ${cur.startsWith('url:') ? 'selected' : ''}>Un lien (https)…</option>`;
  function optionsPanel(): Html {
    const f = findBlock(c, c.sel);
    if (!f) return html`<p class="se-note">Choisis un bloc sur la page : ses options s’affichent ici.</p>`;
    const b = f.block, live = isLive(b.type);
    const headH = html`<div class="se-bhead ${live ? 'se-lv' : ''}"><span class="se-ti">${ico(BLOCKS[b.type].icon)}</span><div><b>${BLOCKS[b.type].label}</b>
      <small>${live ? html`<span class="se-live-dot">Bloc vivant</span> se remplit tout seul` : 'Bloc fixe · tu écris, tu choisis'}</small></div></div>`;
    const parts: (Html | '')[] = [headH];
    const sp = () => html`<div class="se-opt"><div class="lab">Espacement</div>${seg('o.sp', [['s', 'Serré'], ['m', 'Normal'], ['l', 'Large']], b.o.sp ?? 'm')}</div>`;
    const imgOpt = (label: string) => html`<div class="se-opt"><div class="lab">${label} <small>clic sur l’image pour la changer</small></div>
      <div class="se-row"><span style="width:64px;height:42px;border-radius:8px;overflow:hidden;display:block;flex:none">${typeof b.c.img === 'string' && b.c.img
        ? html`<img data-media="${b.c.img}" alt="" style="width:100%;height:100%;object-fit:cover">` : artCourt(c.club.color, `opt${b.id}`)}</span>
      <button type="button" class="se-txtbtn" data-pick="img">${ico('image')}Changer</button>${b.c.img ? html`<button type="button" class="se-txtbtn" data-set="c.img" data-val="null">Retirer</button>` : ''}</div></div>`;
    switch (b.type) {
      case 'text': {
        parts.push(variants(b));
        parts.push(html`<div class="se-opt"><div class="lab">Fond</div><div class="se-swatches">${([['image', 'Image', 'linear-gradient(135deg,#1B2436,#B9773E)'], ['club', 'Club', c.club.color ?? '#0F4C81'],
          ['sombre', 'Sombre', '#0B1220'], ['clair', 'Clair', '#F3F4F6'], ['blanc', 'Blanc', '#fff']] as const).filter(([k]) => k !== 'image' || b.variant === 'banniere').map(([k, l, bg]) =>
          html`<button type="button" class="se-sw" data-set="o.bg" data-val="${JSON.stringify(k)}" aria-pressed="${String((b.o.bg ?? 'blanc') === k)}"><i style="background:${bg}"></i>${l}</button>`)}</div></div>`);
        if ((b.variant === 'banniere' && b.o.bg === 'image') || b.variant === 'image') parts.push(imgOpt(b.variant === 'image' ? 'Image' : 'Image du fond'));
        if (b.variant === 'banniere') parts.push(html`<div class="se-opt"><div class="lab">Hauteur</div>${seg('o.h', [['m', 'Moyenne'], ['l', 'Grande'], ['full', 'Plein écran']], b.o.h ?? 'l')}</div>`);
        parts.push(sp());
        const btns = (Array.isArray(b.c.btns) ? b.c.btns : []) as { l?: string; to?: string }[];
        parts.push(html`<div class="se-opt"><div class="lab">Boutons <small>2 au plus</small></div><div class="se-list-edit">${btns.map((x, i) => html`<div>
          <input type="text" maxlength="40" value="${x.l ?? ''}" data-input="c.btns.${i}.l" aria-label="Texte du bouton">
          <div class="se-row"><select data-target="${i}" aria-label="Où mène le bouton">${pageTargets(x.to ?? '')}</select><button type="button" class="se-iconbtn" data-btn-del="${i}" aria-label="Retirer le bouton">${ico('trash')}</button></div>
          ${String(x.to ?? '').startsWith('url:') ? html`<input type="url" placeholder="https://…" value="${String(x.to).slice(4)}" data-url="${i}" aria-label="Lien https">` : ''}
          <small class="muted">→ ${linkLabel(x.to ?? '', c.doc)}</small></div>`)}</div>
          ${btns.length < 2 ? html`<button type="button" class="se-txtbtn" data-btn-add>${ico('plus')}Ajouter un bouton</button>` : ''}</div>`);
        break;
      }
      case 'next':
        parts.push(html`<p class="hint">Le prochain match à domicile (sinon le prochain match), de l’app et de la FFBB. Jamais le nom d’un joueur ou d’une joueuse.</p>`);
        parts.push(html`<div class="se-opt"><div class="lab">Afficher</div><div>${tog('o.follow', 'Bouton « ☆ Suivre »', 'le groupe du match', b.o.follow !== false)}${tog('o.agenda', 'Ajouter à mon agenda', 'un fichier pour le téléphone', b.o.agenda !== false)}</div></div>`);
        break;
      case 'homes':
        parts.push(html`<p class="hint">Matchs de l’app et de la FFBB : un match déplacé ou ajouté s’affiche tout seul.</p>`);
        parts.push(groupChips(b));
        parts.push(html`<div class="se-opt"><div class="lab">Nombre de matchs</div>${seg('o.n', [[2, '2'], [4, '4'], [6, '6'], [0, 'Tous']], b.o.n ?? 4)}</div>`);
        parts.push(variants(b));
        parts.push(html`<div class="se-opt"><div class="lab">Afficher</div><div>${tog('o.entry', 'Entrée et tarif', 'comme réglés dans l’app', b.o.entry !== false)}${tog('o.filter', 'Filtre par groupe', '', b.o.filter !== false)}
          ${tog('o.follow', 'Bouton « ☆ Suivre »', 'sur chaque groupe', b.o.follow === true)}</div></div>`);
        parts.push(sp());
        break;
      case 'away': case 'results':
        parts.push(groupChips(b));
        parts.push(html`<div class="se-opt"><div class="lab">Nombre</div>${seg('o.n', b.type === 'away' ? [[3, '3'], [6, '6'], [0, 'Tous']] : [[3, '3'], [6, '6'], [12, '12']], b.o.n ?? 6)}</div>`);
        parts.push(variants(b), sp());
        break;
      case 'teams': parts.push(groupChips(b), sp()); break;
      case 'trial':
        parts.push(html`<p class="hint">Le formulaire d’essai de la page du club : le bureau et les coachs de la catégorie sont prévenus.</p>`, variants(b));
        if (b.variant !== 'centre') parts.push(imgOpt('Photo'));
        parts.push(sp());
        break;
      case 'partners':
        parts.push(html`<p class="hint">Les partenaires de l’app, affichés sur la page publique (réglage de chaque fiche partenaire).</p>`, variants(b));
        parts.push(html`<div class="se-opt"><div class="lab">Afficher</div><div>${tog('o.become', '« Devenir partenaire »', 'le formulaire de la page du club', b.o.become !== false)}${tog('o.donate', 'Faire un don au club', 'le lien de don de la page du club', b.o.donate !== false)}</div></div>`, sp());
        break;
      case 'agenda': parts.push(html`<div class="se-opt"><div class="lab">Nombre de rendez-vous</div>${seg('o.n', [[2, '2'], [4, '4'], [6, '6']], b.o.n ?? 4)}</div>`, sp()); break;
      case 'news':
        parts.push(html`<p class="hint">Les actualités écrites dans l’app (Site du club › Actualités), les plus récentes d’abord.</p>`);
        parts.push(html`<div class="se-opt"><div class="lab">Nombre</div>${seg('o.n', [[3, '3'], [6, '6']], b.o.n ?? 3)}</div>`, variants(b));
        parts.push(html`<div class="se-opt"><div>${tog('o.photos', 'Victoires en images', 'les selfies publiés par le staff, avec l’accord', b.o.photos !== false)}</div></div>`, sp());
        break;
      case 'shop':
        parts.push(html`<p class="hint">Des liens de paiement saisis dans l’app (HelloAsso, cotisation, don) : le paiement se fait chez l’organisme, Arceau n’encaisse rien.</p>
          <a class="se-txtbtn" href="#/club/site/boutique" data-leave>${ico('shop')}Les liens de paiement (${(c.st.site?.shop ?? []).length}) ›</a>`, sp());
        break;
      case 'follow': parts.push(html`<p class="hint">Une pastille par groupe : le visiteur suit son groupe (résultats, changements) sans être membre du club.</p>`); break;
      case 'image': parts.push(variants(b), imgOpt('Photo'), sp()); break;
      case 'gallery': {
        const imgs = (Array.isArray(b.c.imgs) ? b.c.imgs : []) as { id?: string }[];
        parts.push(variants(b), html`<div class="se-opt"><div class="lab">Photos <small>${imgs.length} / 24</small></div><div class="se-lib">${imgs.map((im, i) =>
          html`<button type="button" data-gal-del="${i}" aria-label="Retirer la photo ${i + 1}" title="Retirer"><img data-media="${im.id ?? ''}" alt=""></button>`)}</div>
          ${imgs.length < 24 ? html`<button type="button" class="se-txtbtn" data-gal-add>${ico('plus')}Ajouter une photo</button>` : ''}<small class="muted">Touche une photo pour la retirer.</small></div>`, sp());
        break;
      }
      case 'video':
        parts.push(variants(b), html`<div class="se-opt"><div class="lab">Lien YouTube</div><input type="url" data-yt placeholder="https://www.youtube.com/watch?v=…" value="${b.c.yt ? `https://youtu.be/${String(b.c.yt)}` : ''}">
          <small class="muted">Les vidéos se mettent sur YouTube, jamais en fichier ; rien n’est chargé avant que le visiteur appuie.</small></div>`, sp());
        break;
      case 'button':
        parts.push(variants(b), html`<div class="se-opt"><div class="lab">Où mène le bouton</div><select data-target="-1">${pageTargets(String(b.c.to ?? ''))}</select>
          ${String(b.c.to ?? '').startsWith('url:') ? html`<input type="url" placeholder="https://…" value="${String(b.c.to).slice(4)}" data-url="-1">` : ''}</div>`, sp());
        break;
      case 'figures': case 'faq': case 'docs': {
        const items = (Array.isArray(b.c.items) ? b.c.items : []) as Record<string, string>[];
        const max = b.type === 'figures' ? 6 : 20;
        const name = (it: Record<string, string>, i: number) => (b.type === 'figures' ? `${it.n ?? ''} ${it.t ?? ''}` : b.type === 'faq' ? it.q ?? `Question ${i + 1}` : it.l ?? 'Document');
        if (b.type === 'figures') parts.push(variants(b));
        parts.push(html`<div class="se-opt"><div class="lab">${b.type === 'figures' ? 'Chiffres' : b.type === 'faq' ? 'Questions' : 'Documents (PDF, 10 Mo au plus)'} <small>${items.length} / ${max}</small></div>
          <ul class="se-pagelist">${items.map((it, i) => html`<li><button type="button" data-item-del="${i}"><span>${plainText(name(it, i)) || '—'}</span><small>retirer</small></button></li>`)}</ul>
          ${items.length < max ? html`<button type="button" class="se-txtbtn" data-item-add>${ico('plus')}${b.type === 'docs' ? 'Ajouter un PDF' : 'Ajouter'}</button>` : ''}
          <small class="muted">${b.type === 'docs' ? 'Le nom s’écrit sur la page.' : 'Les textes s’écrivent sur la page.'}</small></div>`, sp());
        break;
      }
      case 'map': parts.push(html`<div class="se-opt"><div class="lab">Adresse</div><input type="text" maxlength="160" data-input="c.addr" value="${String(b.c.addr ?? '')}" placeholder="${c.club.address ?? 'Gymnase…'}">
        <small class="muted">Un plan dessiné et « Ouvrir le plan » (OpenStreetMap) : aucune carte en ligne n’est chargée sur le site.</small></div>`, sp()); break;
      case 'contact': parts.push(variants(b), html`<div class="se-opt"><div class="lab">Afficher</div><div>${tog('o.bureau', 'Le bureau', 'les fonctions « Sur la page du club »', b.o.bureau !== false)}${tog('o.slots', 'Les salles d’entraînement', '', b.o.slots !== false)}</div></div>`, sp()); break;
    }
    return html`${parts}`;
  }

  function pagePanel(): Html {
    const g = groupOfPage(c), p = pageOf(c);
    const host = publicHost(c);
    const google = (title: string, desc: string, path: string) => html`<div class="se-g"><div class="se-g-site">${clubMark(ctx.club, 'sm')}<span>${c.club.name}<small>https://${host}${path ? ` › ${path.split('/').join(' › ')}` : ''}</small></span></div>
      <div class="se-g-t">${title}</div><div class="se-g-d">${desc}</div></div>`;
    const counter = (n: number, max: number) => html`<span class="se-count ${n > max ? 'ko' : ''}">${n} / ${max}</span>`;
    if (g) {
      const t = siteGroups({ data: c.data }).find(x => x.id === g.id)!;
      const next = (c.data?.pub.upcoming ?? []).find(m => m.is_home && m.group_id === g.id);
      const date = next ? new Intl.DateTimeFormat('fr-FR', { timeZone: c.club.timezone, day: 'numeric', month: 'long' }).format(new Date(next.starts_at)) : '';
      const seo = c.doc.teams.seo[g.id] ?? {};
      const pt = seoTitle(g.name, c.club.name), pd = seoTeamDesc(t.slots, next ? { versus: `${next.team} – ${next.opponent}`, date } : null);
      const title = seo.title || pt, desc = seo.desc || pd;
      return html`<div class="se-bhead"><span class="se-ti">${ico('doc')}</span><div><b>Page « ${g.name} »</b><small>Générée depuis l’app, dans le menu Équipes</small></div></div>
        <div class="se-opt"><div class="lab">Adresse de la page</div><input type="text" value="${host}/${groupSlug(g.name)}" readonly aria-label="Adresse de la page"></div>
        <div class="se-ph"><h4>Sur Google</h4><small>proposé par Arceau, modifiable</small></div>
        <div class="se-opt"><div class="lab">Titre ${counter(title.length, SEO_TITLE_MAX)}</div><input type="text" maxlength="70" data-team-seo="title" value="${seo.title ?? ''}" placeholder="${pt}"></div>
        <div class="se-opt"><div class="lab">Description ${counter(desc.length, SEO_DESC_MAX)}</div><textarea maxlength="200" data-team-seo="desc" placeholder="${pd}">${seo.desc ?? ''}</textarea></div>
        ${google(title, desc, groupSlug(g.name))}
        <p class="hint">La description suit les créneaux et le prochain match tant que tu ne la modifies pas.</p>
        <div>${tog('team-hide', 'Cacher cette page', 'elle revient d’un geste', c.doc.teams.hidden.includes(g.id))}</div>`;
    }
    if (!p) return html``;
    if (p.kind === 'legal') {
      const miss = c.legal ? legalMissing(c.legal) : [];
      return html`<div class="se-bhead"><span class="se-ti">${ico('lock')}</span><div><b>Page « Mentions légales »</b><small>Générée · se met à jour toute seule</small></div></div>
        <p class="se-note">Elle ne s’écrit pas ici : Arceau la compose avec <b>Club › Page du club › Réglages › Mentions légales et données</b> (nom officiel, siège, représentant légal) et les fonctions du bureau. L’hébergeur est IDR.</p>
        ${miss.length ? html`<p class="warn">Il manque ${miss.join(', ')} : la page affiche « à compléter ».</p>` : ''}
        ${c.st.via === 'bureau' ? html`<a class="btn btn-ghost" href="#/club/page/reglages/mentions" data-leave>Compléter dans l’app ›</a>` : ''}
        <div class="se-ph"><h4>Sur tout le site</h4></div>
        <div class="se-tog"><span>Lien en pied de chaque page<small>obligatoire, toujours là</small></span>${ico('lock')}</div>`;
    }
    const home = p.kind === 'home';
    const pt = seoTitle(p.title, c.club.name, c.club.tagline, home), pd = seoDesc(p, c.club.tagline ?? c.club.name);
    const title = p.seo.title || pt, desc = p.seo.desc || pd;
    const removable = !home;
    return html`<div class="se-bhead"><span class="se-ti">${ico('doc')}</span><div><b>Page « ${p.title} »</b><small>${p.menu ? 'Dans le menu du site' : 'Hors du menu'}</small></div></div>
      <div class="se-opt"><div class="lab">Nom de la page</div><input type="text" maxlength="60" data-page="title" value="${p.title}"></div>
      <div class="se-opt"><div class="lab">Adresse de la page</div>${home ? html`<input type="text" value="${host}" readonly aria-label="Adresse de la page">`
        : html`<div class="se-row"><span class="muted small">${host}/</span><input type="text" maxlength="40" data-page="slug" value="${p.slug}" aria-label="Adresse de la page"></div>`}</div>
      ${home ? '' : tog('page-menu', 'Dans le menu', '', p.menu)}
      <div class="se-ph"><h4>Sur Google</h4><small>proposé par Arceau, modifiable</small></div>
      <div class="se-opt"><div class="lab">Titre ${counter(title.length, SEO_TITLE_MAX)}</div><input type="text" maxlength="70" data-page="seo.title" value="${p.seo.title ?? ''}" placeholder="${pt}"></div>
      <div class="se-opt"><div class="lab">Description ${counter(desc.length, SEO_DESC_MAX)}</div><textarea maxlength="200" data-page="seo.desc" placeholder="${pd}">${p.seo.desc ?? ''}</textarea></div>
      ${google(title, desc, p.slug)}
      <p class="hint">Vides, le titre et la description sont proposés par Arceau d’après la page.</p>
      ${removable ? html`<button type="button" class="btn btn-danger-ghost btn-sm" id="se-page-del">Retirer cette page</button>` : ''}`;
  }

  function themePanel(): Html {
    return html`<div class="se-bhead"><span class="se-ti">${ico('palette')}</span><div><b>Thème du site</b><small>Il habille tout le site ; les pages, les blocs et les textes restent.</small></div></div>
      <div class="se-themes" style="grid-template-columns:1fr">${SITE_THEMES.map(t => html`<button type="button" class="se-theme" data-theme="${t.key}" aria-pressed="${String(c.doc.theme === t.key)}">
        <span class="se-theme-t"><b>${t.name}${c.doc.theme === t.key ? html` <span class="chip chip-ok">Choisi</span>` : ''}</b><small>${t.desc}</small></span></button>`)}</div>
      <div class="se-brandrow">${clubMark(ctx.club, 'md')}<span><i style="background:${c.club.color ?? '#0F4C81'}"></i> <i style="background:${c.st.color2 ?? '#F28C28'}"></i></span>
        <span><b>Couleurs et logo du club</b><br><span class="muted">déjà dans l’app</span></span>${c.st.via === 'bureau' ? html`<a href="#/club/identite" data-leave>Modifier ›</a>` : ''}</div>`;
  }

  async function drawHistory(): Promise<void> {
    versions ??= await siteVersions(c.clubId).catch(() => []);
    const n = notes(c);
    const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
    mount(panel, html`<div class="se-pbody">
      <div class="se-bhead"><span class="se-ti">${ico('hist')}</span><div><b>Historique des versions</b><small>Chaque publication est gardée</small></div></div>
      <ul class="se-vers" data-guide="site-versions">
        ${n.length ? html`<li class="draft"><i></i><div><b>Brouillon en cours</b><small>${c.st.site?.draft_by ?? ''} · ${n.length} ${n.length > 1 ? 'changements' : 'changement'} : ${n.slice(0, 4).join(', ')}${n.length > 4 ? '…' : ''}</small></div></li>` : ''}
        ${(versions ?? []).map(v => html`<li class="se-vp ${readonly?.id === v.id ? 'on' : ''}"><i></i><div><button type="button" class="se-vlink" data-ver="${v.id}">
          <b>${v.kind === 'creation' ? 'Site créé' : 'Publiée'} · ${when(v.created_at)}${v.online ? ' · en ligne' : ''}</b>
          <small>${v.by_label}${v.notes.length ? ` · ${v.notes.slice(0, 3).join(', ')}${v.notes.length > 3 ? '…' : ''}` : v.kind === 'creation' ? ` · thème ${themeName(c.doc.theme)}` : ''}</small></button>
          ${readonly?.id === v.id ? html`<div class="acts"><button type="button" class="btn btn-primary btn-sm" data-restore="${v.id}">Revenir à cette version</button></div>` : ''}</div></li>`)}
      </ul>
      <p class="se-note">Revenir à une version en fait un <b>brouillon</b> : rien ne change en ligne avant « Publier ». Annuler et rétablir (en haut) défont les derniers gestes du brouillon.</p>
      <button type="button" class="btn btn-ghost btn-sm" id="se-hist-close">Fermer l’historique</button></div>`);
  }

  // --- Les pages et le menu (s3) ----------------------------------------------------------------------------------------
  function openPages(anchor: HTMLElement): void {
    document.querySelector('.se-dd')?.remove();
    const groups = c.doc.pages.some(p => p.kind === 'teams') ? teamPages(c.doc, siteGroups({ data: c.data })) : [];
    const r = anchor.getBoundingClientRect();
    const dd = document.createElement('div');
    dd.className = 'se-dd';
    dd.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - 412))}px`; dd.style.top = `${r.bottom + 6}px`;
    dd.innerHTML = html`<div class="se-dd-h"><h4>Pages et menu du site</h4><small>glisse pour l’ordre du menu</small></div>
      <ul data-guide="site-menu">${c.doc.pages.map(p => html`<li draggable="${String(p.kind !== 'legal')}" data-pg="${p.id}" class="${c.page === p.id ? 'on' : ''}">${ico('grip', 'g')}
        <span><b>${p.title}</b><small>${p.kind === 'home' ? publicHost(c) : p.kind === 'legal' ? 'générée · se met à jour toute seule' : p.kind === 'teams' ? `${groups.length} pages, une par groupe · générées` : p.menu ? `/${p.slug}` : `/${p.slug} · hors du menu`}</small></span>
        ${p.kind === 'legal' ? ico('lock') : p.kind === 'teams' ? html`<span class="chip chip-mute">vivantes</span>` : p.kind === 'home' && online(c) ? html`<span class="chip chip-ok">En ligne</span>` : ''}</li>
        ${p.kind === 'teams' ? groups.map(g => html`<li class="se-sub ${c.page === `team-${g.id}` ? 'on' : ''}" draggable="true" data-tg="${g.id}"><span>${ico('grip', 'g')}</span><span><b>${g.name}</b></span><span></span></li>`) : ''}`)}</ul>
      <button type="button" class="btn btn-ghost" id="se-newpage" style="min-height:40px">${ico('plus')} Nouvelle page</button>`.s;
    document.body.append(dd);
    const close = () => { dd.remove(); document.removeEventListener('pointerdown', outside, true); };
    const outside = (e: Event) => { if (!dd.contains(e.target as Node) && e.target !== anchor && !anchor.contains(e.target as Node)) close(); };
    setTimeout(() => document.addEventListener('pointerdown', outside, true), 0);
    let dragPg: string | null = null, dragTg: string | null = null;
    for (const li of $$<HTMLElement>(dd, 'li')) {
      li.addEventListener('click', () => { c.page = li.dataset.pg ?? `team-${li.dataset.tg}`; c.sel = null; close(); drawTop(); drawCanvas(); drawPanel(); });
      li.addEventListener('dragstart', (e) => { dragPg = li.dataset.pg ?? null; dragTg = li.dataset.tg ?? null; e.dataTransfer?.setData('text/plain', 'se-page'); });
      li.addEventListener('dragover', (e) => { e.preventDefault(); $$(dd, 'li.over').forEach(x => x.classList.remove('over')); li.classList.add('over'); });
      li.addEventListener('drop', (e) => {
        e.preventDefault();
        if (dragPg && li.dataset.pg && dragPg !== li.dataset.pg && li.dataset.pg !== c.doc.pages.find(p => p.kind === 'legal')?.id) {
          change(c, d => { const from = d.pages.findIndex(p => p.id === dragPg); const [m] = d.pages.splice(from, 1); d.pages.splice(d.pages.findIndex(p => p.id === li.dataset.pg), 0, m!); });
        } else if (dragTg && li.dataset.tg && dragTg !== li.dataset.tg) {
          change(c, d => { const order = groups.map(g => g.id); const from = order.indexOf(dragTg!); order.splice(from, 1); order.splice(order.indexOf(li.dataset.tg!), 0, dragTg!); d.teams.order = order; });
        }
        close(); openPages(anchor);
      });
    }
    $(dd, '#se-newpage')!.addEventListener('click', () => { close(); newPage(); });
  }
  function newPage(): void {
    openSheet('Nouvelle page', html`<form class="form" id="se-np" novalidate><label class="field"><span>Nom de la page</span><input name="t" maxlength="60" placeholder="ex. Nos stages" required></label>
      <p class="hint">Elle arrive dans le menu avec un titre et un texte ; ajoute ensuite tes blocs.</p><p class="form-msg" role="alert"></p><button class="btn btn-primary" type="submit">Créer la page</button></form>`, (sheet, close) => {
      const f = $<HTMLFormElement>(sheet, '#se-np')!;
      f.addEventListener('submit', (e) => {
        e.preventDefault();
        const t = String(new FormData(f).get('t') ?? '').trim();
        if (!t) { $(f, '.form-msg')!.textContent = 'Donne un nom à la page.'; return; }
        let slug = slugify(t).slice(0, 36) || 'page';
        if (RESERVED_PAGE_SLUGS.includes(slug)) slug = `${slug}-club`;
        while (c.doc.pages.some(p => p.slug === slug)) slug = `${slug.slice(0, 34)}-${Math.floor(Math.random() * 90 + 10)}`;
        const id = newId('p');
        const ok = change(c, d => { d.pages.splice(d.pages.findIndex(p => p.kind === 'legal'), 0, { id, kind: 'custom', slug, title: t, menu: true, seo: {},
          blocks: [newBlock('text', { variant: 'banniere', o: { bg: 'club', sp: 'm', h: 'm' }, c: { k: c.club.name, t, b: 'Votre texte ici.', btns: [] } })] }); });
        if (ok) { c.page = id; c.sel = null; close(); drawTop(); drawCanvas(); drawPanel(); }
      });
    });
  }

  // --- L'écriture sur la page ------------------------------------------------------------------------------------------
  let tbar: HTMLElement | null = null;
  function startEdit(el: HTMLElement): void {
    if (el.isContentEditable) return;
    const secEl = el.closest<HTMLElement>('[data-bid]');
    if (!secEl) return;
    const id = secEl.dataset.bid!, path = el.dataset.edit!, rich = !!el.dataset.rich, inline = el.dataset.rich === 'inline';
    if (c.sel !== id) { c.sel = id; drawPanel(); canvas.querySelectorAll('.se-sel').forEach(x => x.classList.remove('se-sel')); canvas.querySelector('.se-bar')?.remove(); decorate(); }
    const target = canvas.querySelector<HTMLElement>(`[data-bid="${id}"] [data-edit="${path}"]`) ?? el;
    target.contentEditable = rich ? 'true' : 'plaintext-only';
    if (!target.isContentEditable) target.contentEditable = 'true';
    target.classList.add('se-editing');
    target.focus();
    const range = document.createRange(); range.selectNodeContents(target); range.collapse(false);
    const s = window.getSelection(); s?.removeAllRanges(); s?.addRange(range);
    if (rich) showTbar(target);
    const finish = () => {
      target.removeEventListener('blur', onBlur);
      tbar?.remove(); tbar = null;
      const value = rich ? (inline ? toMarkup(target).replace(/\s*\n+\s*/g, ' ') : toMarkup(target)) : (target.innerText ?? '').replace(/\s+/g, ' ').trim();
      target.contentEditable = 'false'; target.classList.remove('se-editing');
      const f = findBlock(c, id);
      if (!f) return;
      if (String(getPath(f.block.c, path) ?? '') === value) { drawCanvas(); return; }
      if (!change(c, d => { const b = d.pages.flatMap(p => p.blocks).find(x => x.id === id)!; setPath(b.c, path, value); })) drawCanvas();
    };
    const onBlur = (e: FocusEvent) => { if (tbar && e.relatedTarget && tbar.contains(e.relatedTarget as Node)) return; finish(); };
    target.addEventListener('blur', onBlur);
    target.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || (e.key === 'Enter' && (!rich || inline))) { e.preventDefault(); target.blur(); }
    });
  }
  function showTbar(target: HTMLElement): void {
    tbar?.remove();
    tbar = document.createElement('div');
    tbar.className = 'se-tbar';
    tbar.innerHTML = html`<button type="button" class="on" tabindex="-1">${target.dataset.rich === 'inline' ? 'Titre' : 'Texte'}</button><span class="sep"></span>
      <button type="button" data-cmd="bold" title="Gras" aria-label="Gras">${ico('bold')}</button><button type="button" data-cmd="italic" title="Italique" aria-label="Italique">${ico('italic')}</button>
      <button type="button" data-cmd="link">${ico('link')}Lien</button><span class="sep"></span><button type="button" data-cmd="color"><span class="se-dot"></span>Couleur</button>`.s;
    canvas.append(tbar);
    const r = target.getBoundingClientRect(), cr = canvas.getBoundingClientRect();
    tbar.style.left = `${Math.max(4, r.left - cr.left)}px`;
    tbar.style.top = `${Math.max(4, r.top - cr.top + canvas.scrollTop - 50)}px`;
    tbar.addEventListener('mousedown', e => e.preventDefault());
    tbar.addEventListener('click', (e) => {
      const cmd = (e.target as HTMLElement).closest<HTMLElement>('[data-cmd]')?.dataset.cmd;
      if (!cmd) return;
      if (cmd === 'bold' || cmd === 'italic') document.execCommand(cmd);
      else if (cmd === 'link') {
        const url = window.prompt('Le lien (https://…)', 'https://');
        if (url && /^https:\/\/\S+$/.test(url)) document.execCommand('createLink', false, url);
        else if (url) toast('Un lien https:// seulement.', 'ko');
      } else if (cmd === 'color') {
        const sel = window.getSelection()?.toString();
        if (sel) document.execCommand('insertHTML', false, `<mark class="sw-hl">${sel.replace(/[&<>"']/g, ch => `&#${ch.charCodeAt(0)};`)}</mark>`);
      }
      target.focus();
    });
  }

  // --- Les gestes ------------------------------------------------------------------------------------------------------
  top.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('#se-undo')) undo(c);
    else if (t.closest('#se-redo')) redo(c);
    else if (t.closest('#se-pub')) void publish(c, false);
    else if (t.closest('#se-eye')) { preview = !preview; drawTop(); drawCanvas(); }
    else if (t.closest('#se-hist')) { history = !history; readonly = null; versions = null; drawTop(); drawCanvas(); drawPanel(); }
    else if (t.closest('#se-pages')) openPages(t.closest('#se-pages')!);
    else if (t.closest('[data-dev]')) { device = t.closest<HTMLElement>('[data-dev]')!.dataset.dev as 'desk' | 'phone'; drawTop(); drawCanvas(); }
    else if (t.closest('#se-quit')) { e.preventDefault(); void flush(c).then(() => { location.hash = '#/club/site'; }); }
  });
  canvas.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-close-ro]')) { readonly = null; drawCanvas(); drawPanel(); return; }
    const rs = t.closest<HTMLElement>('[data-restore]');
    if (rs) { void restore(rs); return; }
    const bar = t.closest<HTMLElement>('[data-bar]');
    if (bar) { barAction(bar.dataset.bar!); return; }
    if (tbar?.contains(t)) return;
    const a = t.closest<HTMLAnchorElement>('a, button');
    if (a && !t.closest('.se-tbar')) e.preventDefault();
    if (readonly || preview) {
      const href = a?.getAttribute('href') ?? '';
      if (href.startsWith('#se:')) goPath(href.slice(4));
      return;
    }
    const edit = t.closest<HTMLElement>('[data-edit]');
    if (edit && !edit.isContentEditable) { startEdit(edit); return; }
    if (edit) return;
    const img = t.closest<HTMLElement>('[data-img]');
    const blockEl = t.closest<HTMLElement>('[data-bid]');
    if (img && blockEl) { c.sel = blockEl.dataset.bid!; void changeImage(blockEl.dataset.bid!, img.dataset.img!); return; }
    if (blockEl) { select(blockEl.dataset.bid!); return; }
    const href = a?.getAttribute('href') ?? '';
    if (href.startsWith('#se:')) goPath(href.slice(4));
  });
  /** Un lien du site dans la toile : la page qu'il ouvre, dans l'éditeur. */
  function goPath(path: string): void {
    const p = c.doc.pages.find(x => x.slug === path);
    const g = siteGroups({ data: c.data }).find(x => groupSlug(x.name) === path);
    if (p) c.page = p.id; else if (g) c.page = `team-${g.id}`; else return;
    c.sel = null; drawTop(); drawCanvas(); drawPanel();
  }
  async function changeImage(id: string, path: string): Promise<void> {
    const f = findBlock(c, id);
    if (!f) return;
    const cur = getPath(f.block.c, path);
    const pid = await pickPhoto(c.clubId, 'Changer la photo', typeof cur === 'string' ? cur : null);
    if (!pid) { drawCanvas(); drawPanel(); return; }
    change(c, d => {
      const b = d.pages.flatMap(p => p.blocks).find(x => x.id === id)!;
      setPath(b.c, path, pid);
      if (b.type === 'text' && b.variant === 'banniere' && path === 'img') b.o.bg = 'image';
    });
  }
  function barAction(a: string): void {
    const f = findBlock(c, c.sel);
    if (!f) return;
    if (a === 'opts') { tab = 'Options'; history = false; drawTop(); drawPanel(); drawCanvas(); return; }
    if (a === 'del') {
      const btn = canvas.querySelector<HTMLElement>('[data-bar="del"]');
      if (btn && !btn.dataset.armed) { btn.dataset.armed = '1'; btn.innerHTML = 'Supprimer ?'; return; }
      change(c, d => { const p = d.pages.find(x => x.id === f.page.id)!; p.blocks.splice(f.i, 1); });
      c.sel = null; drawPanel(); drawCanvas();
      return;
    }
    change(c, d => {
      const p = d.pages.find(x => x.id === f.page.id)!;
      if (a === 'up' && f.i > 0) [p.blocks[f.i - 1], p.blocks[f.i]] = [p.blocks[f.i]!, p.blocks[f.i - 1]!];
      if (a === 'down' && f.i < p.blocks.length - 1) [p.blocks[f.i + 1], p.blocks[f.i]] = [p.blocks[f.i]!, p.blocks[f.i + 1]!];
      if (a === 'dup') { const copy: SiteBlock = { ...JSON.parse(JSON.stringify(f.block)), id: newId('b') }; p.blocks.splice(f.i + 1, 0, copy); c.sel = copy.id; }
    });
    requestAnimationFrame(() => canvas.querySelector('.se-sel')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }
  async function restore(btn: HTMLElement): Promise<void> {
    if (!armed(btn, 'Revenir : sûr ?')) return;
    await flush(c);
    try {
      await siteRestore(btn.dataset.restore!);
      const st = await siteState(c.clubId);
      if (st?.site) { c.st = st; c.doc = JSON.parse(JSON.stringify(st.site.draft)); c.rev = st.site.rev; c.undo = []; c.redo = []; }
      readonly = null; history = false;
      toast('C’est ton brouillon : rien ne change en ligne avant « Publier ».');
      drawTop(); drawCanvas(); drawPanel();
    } catch { toast('Impossible pour le moment.', 'ko'); }
  }
  /** Poser un bloc : à l'endroit de dépôt, sinon sous le bloc choisi (ou en bas de la page). */
  function addBlock(type: BlockType, at: number | null): void {
    const p = pageOf(c);
    if (!p || p.kind === 'legal' || groupOfPage(c)) { toast('Cette page est générée : choisis une autre page pour poser des blocs.', 'ko'); return; }
    const sel = findBlock(c, c.sel);
    const index = at ?? (sel && sel.page.id === p.id ? sel.i + 1 : p.blocks.length);
    const b = newBlock(type);
    if (change(c, d => { d.pages.find(x => x.id === p.id)!.blocks.splice(index, 0, b); })) {
      c.sel = b.id; tab = isLive(type) ? 'Options' : tab;
      drawPanel(); drawCanvas();
      requestAnimationFrame(() => canvas.querySelector('.se-sel')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  }
  panel.addEventListener('input', (e) => {
    const t = e.target as HTMLInputElement;
    if (t.id === 'se-q') { search = t.value; const pos = t.selectionStart; drawPanel(); const q = $<HTMLInputElement>(panel, '#se-q'); q?.focus(); q?.setSelectionRange(pos, pos); }
  });
  panel.addEventListener('change', (e) => {
    const t = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const f = findBlock(c, c.sel);
    if (t.dataset.input && f) change(c, d => { setPath(d.pages.flatMap(p => p.blocks).find(x => x.id === f.block.id)! as unknown as Record<string, unknown>, t.dataset.input!, t.value.trim()); });
    else if (t.dataset.target !== undefined && f) {
      const i = Number(t.dataset.target), v = t.value === 'url' ? 'url:https://' : t.value;
      change(c, d => { const b = d.pages.flatMap(p => p.blocks).find(x => x.id === f.block.id)!; if (i < 0) b.c.to = v; else setPath(b.c, `btns.${i}.to`, v); });
      drawPanel();
    } else if (t.dataset.url !== undefined && f) {
      const u = t.value.trim();
      if (!/^https:\/\/[^\s<>"'`]{1,300}$/.test(u)) { toast('Un lien https:// seulement.', 'ko'); return; }
      const i = Number(t.dataset.url);
      change(c, d => { const b = d.pages.flatMap(p => p.blocks).find(x => x.id === f.block.id)!; if (i < 0) b.c.to = `url:${u}`; else setPath(b.c, `btns.${i}.to`, `url:${u}`); });
    } else if (t.hasAttribute('data-yt') && f) {
      const id = t.value.trim() ? youtubeId(t.value) : '';
      if (id === null) { toast('Ce lien n’est pas une vidéo YouTube.', 'ko'); return; }
      change(c, d => { d.pages.flatMap(p => p.blocks).find(x => x.id === f.block.id)!.c.yt = id; });
    } else if (t.dataset.page) {
      const p = pageOf(c);
      if (!p) return;
      const key = t.dataset.page, val = t.value.trim();
      if (key === 'slug' && (!PAGE_SLUG.test(val) || RESERVED_PAGE_SLUGS.includes(val) || c.doc.pages.some(x => x.id !== p.id && x.slug === val))) {
        toast('Adresse : lettres minuscules, chiffres et tirets, pas déjà prise.', 'ko'); drawPanel(); return;
      }
      if (key === 'title' && !val) { drawPanel(); return; }
      change(c, d => { const x = d.pages.find(y => y.id === p.id)!; if (key === 'seo.title') x.seo.title = val || null; else if (key === 'seo.desc') x.seo.desc = val || null; else (x as unknown as Record<string, string>)[key] = val; });
      drawTop(); drawPanel();
    } else if (t.dataset.teamSeo) {
      const g = groupOfPage(c);
      if (!g) return;
      change(c, d => { const s = d.teams.seo[g.id] ?? {}; s[t.dataset.teamSeo as 'title' | 'desc'] = t.value.trim() || null; d.teams.seo[g.id] = s; });
      drawPanel();
    }
  });
  panel.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const tb = t.closest<HTMLElement>('[data-tab]');
    if (tb) { tab = tb.dataset.tab as Tab; drawPanel(); drawCanvas(); return; }
    const add = t.closest<HTMLElement>('[data-add]');
    if (add) { addBlock(add.dataset.add as BlockType, null); return; }
    const leave = t.closest<HTMLAnchorElement>('[data-leave]');
    if (leave) { e.preventDefault(); void flush(c).then(() => { location.hash = leave.getAttribute('href')!; }); return; }
    const ver = t.closest<HTMLElement>('[data-ver]');
    if (ver) { void (async () => { const doc = await siteVersionDoc(ver.dataset.ver!).catch(() => null); const v = versions?.find(x => x.id === ver.dataset.ver);
      if (doc && v) { readonly = { doc, id: v.id, restore: true, label: `Version du ${new Date(v.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).replace(':', 'h')}` }; drawCanvas(); drawPanel(); } })(); return; }
    const rs = t.closest<HTMLElement>('[data-restore]');
    if (rs) { void restore(rs); return; }
    if (t.closest('#se-hist-close')) { history = false; readonly = null; drawTop(); drawCanvas(); drawPanel(); return; }
    const th = t.closest<HTMLElement>('[data-theme]');
    if (th) { change(c, d => { d.theme = th.dataset.theme as SiteTheme; }); drawPanel(); return; }
    if (t.closest('#se-page-del')) {
      const btn = t.closest<HTMLButtonElement>('#se-page-del')!, p = pageOf(c);
      if (!p || !armed(btn, `Retirer « ${p.title} » : sûr ?`)) return;
      change(c, d => { d.pages = d.pages.filter(x => x.id !== p.id); d.pages.forEach(x => x.blocks.forEach(b => {
        if (Array.isArray(b.c.btns)) b.c.btns = (b.c.btns as { to?: string }[]).filter(x2 => x2.to !== `page:${p.id}`);
        if (b.c.to === `page:${p.id}`) b.c.to = 'trial'; })); });
      c.page = c.doc.pages[0]!.id; c.sel = null; drawTop(); drawCanvas(); drawPanel();
      return;
    }
    const sw = t.closest<HTMLElement>('[data-toggle]');
    if (sw) {
      const path = sw.dataset.toggle!, on = sw.dataset.on === 'true';
      if (path === 'page-menu') { const p = pageOf(c); if (p) change(c, d => { d.pages.find(x => x.id === p.id)!.menu = !on; }); drawTop(); drawPanel(); return; }
      if (path === 'team-hide') { const g = groupOfPage(c); if (g) change(c, d => { d.teams.hidden = on ? d.teams.hidden.filter(x => x !== g.id) : [...d.teams.hidden, g.id]; }); drawPanel(); return; }
      const f = findBlock(c, c.sel);
      if (f) change(c, d => { setPath(d.pages.flatMap(p => p.blocks).find(x => x.id === f.block.id)! as unknown as Record<string, unknown>, path, !on); });
      drawPanel();
      return;
    }
    const f = findBlock(c, c.sel);
    if (!f) return;
    const blk = (d: SiteDoc) => d.pages.flatMap(p => p.blocks).find(x => x.id === f.block.id)!;
    const set = t.closest<HTMLElement>('[data-set]');
    if (set) { change(c, d => { setPath(blk(d) as unknown as Record<string, unknown>, set.dataset.set!, JSON.parse(set.dataset.val!)); }); drawPanel(); return; }
    const vr = t.closest<HTMLElement>('[data-variant]');
    if (vr) { change(c, d => { const b = blk(d); b.variant = vr.dataset.variant!; if (b.type === 'text' && b.variant !== 'banniere' && b.o.bg === 'image') b.o.bg = 'blanc'; }); drawPanel(); return; }
    const gc = t.closest<HTMLElement>('[data-group-chip]');
    if (gc) {
      const all = siteGroups({ data: c.data }).map(g => g.id);
      change(c, d => {
        const b = blk(d); const pick = Array.isArray(b.o.groups) && (b.o.groups as string[]).length ? [...(b.o.groups as string[])] : [...all];
        const id = gc.dataset.groupChip!, next = pick.includes(id) ? pick.filter(x => x !== id) : [...pick, id];
        b.o.groups = next.length === all.length || !next.length ? [] : next;
      });
      drawPanel();
      return;
    }
    if (t.closest('[data-pick]')) { void changeImage(f.block.id, 'img'); return; }
    if (t.closest('[data-btn-add]')) { change(c, d => { const b = blk(d); b.c.btns = [...((b.c.btns as unknown[]) ?? []), { l: 'Venez essayer', to: 'trial' }].slice(0, 2); }); drawPanel(); return; }
    const bd = t.closest<HTMLElement>('[data-btn-del]');
    if (bd) { change(c, d => { const b = blk(d); (b.c.btns as unknown[]).splice(Number(bd.dataset.btnDel), 1); }); drawPanel(); return; }
    if (t.closest('[data-gal-add]')) { void (async () => { const id = await pickPhoto(c.clubId, 'Ajouter une photo'); if (id) change(c, d => { const b = blk(d); b.c.imgs = [...((b.c.imgs as unknown[]) ?? []), { id, cap: '' }]; }); drawPanel(); })(); return; }
    const gd = t.closest<HTMLElement>('[data-gal-del]');
    if (gd) { change(c, d => { (blk(d).c.imgs as unknown[]).splice(Number(gd.dataset.galDel), 1); }); drawPanel(); return; }
    if (t.closest('[data-item-add]')) {
      if (f.block.type === 'docs') { void (async () => { const p = await pickPdf(c.clubId); if (p) change(c, d => { const b = blk(d); b.c.items = [...((b.c.items as unknown[]) ?? []), { id: p.id, l: p.name }]; }); drawPanel(); })(); return; }
      change(c, d => { const b = blk(d); b.c.items = [...((b.c.items as unknown[]) ?? []), b.type === 'figures' ? { n: '10', t: 'à compléter' } : { q: 'Une question ?', a: 'La réponse.' }]; });
      drawPanel();
      return;
    }
    const id = t.closest<HTMLElement>('[data-item-del]');
    if (id) { change(c, d => { (blk(d).c.items as unknown[]).splice(Number(id.dataset.itemDel), 1); }); drawPanel(); }
  });
  // Glisser un bloc du panneau entre deux blocs de la page (s4).
  panel.addEventListener('dragstart', (e) => {
    const tile = (e.target as HTMLElement).closest<HTMLElement>('[data-add]');
    if (!tile) return;
    dragType = tile.dataset.add as BlockType;
    tile.classList.add('drag');
    e.dataTransfer?.setData('text/plain', `se-block:${dragType}`);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'copy';
  });
  panel.addEventListener('dragend', () => { dragType = null; $$(panel, '.se-tile.drag').forEach(x => x.classList.remove('drag')); clearDrop(); });
  let dropAt: number | null = null;
  const clearDrop = () => { canvas.querySelector('.se-drop')?.remove(); canvas.querySelectorAll('.se-hov').forEach(x => x.classList.remove('se-hov')); dropAt = null; };
  canvas.addEventListener('dragover', (e) => {
    if (!dragType) return;
    const p = pageOf(c);
    if (!p || p.kind === 'legal' || groupOfPage(c)) return;
    e.preventDefault();
    const blocks = $$<HTMLElement>(canvas, '.sw-main > [data-bid]');
    let i = blocks.findIndex(b => { const r = b.getBoundingClientRect(); return e.clientY < r.top + r.height / 2; });
    if (i < 0) i = blocks.length;
    if (i === dropAt) return;
    clearDrop(); dropAt = i;
    const z = document.createElement('div');
    z.className = 'se-drop';
    z.innerHTML = html`<span>Déposer ici · ${BLOCKS[dragType].label}</span>`.s;
    const main = canvas.querySelector('.sw-main')!;
    if (blocks[i]) main.insertBefore(z, blocks[i]!); else main.append(z);
    blocks[i - 1]?.classList.add('se-hov');
  });
  canvas.addEventListener('drop', (e) => {
    if (!dragType) return;
    e.preventDefault();
    const t = dragType, at = dropAt;
    dragType = null; clearDrop();
    addBlock(t, at);
  });
  const onKey = (e: KeyboardEvent) => {
    if (!document.body.contains(shell)) { document.removeEventListener('keydown', onKey); return; }
    if ((e.target as HTMLElement).isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName)) return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(c); else undo(c); }
    else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(c); }
  };
  document.addEventListener('keydown', onKey);
  window.addEventListener('hashchange', () => void flush(c), { once: true });

  drawTop(); drawCanvas(); drawPanel();
}

// =====================================================================================================================
// Au téléphone (s5) : textes, photos, ordre des blocs, publier
// =====================================================================================================================
const PHOTO_TYPES = new Set<BlockType>(['text', 'trial', 'image']);
const TEXT_FIELDS: Partial<Record<BlockType, [string, string, boolean][]>> = {
  text: [['k', 'Sur-titre', false], ['t', 'Titre', false], ['b', 'Texte', true]],
  trial: [['t', 'Titre', false], ['b', 'Texte', true]],
  follow: [['t', 'Titre', false], ['b', 'Texte', true]],
  homes: [['k', 'Sur-titre', false], ['t', 'Titre', false]], results: [['k', 'Sur-titre', false], ['t', 'Titre', false]],
  away: [['k', 'Sur-titre', false], ['t', 'Titre', false]], teams: [['k', 'Sur-titre', false], ['t', 'Titre', false]],
  partners: [['k', 'Sur-titre', false], ['t', 'Titre', false]], agenda: [['k', 'Sur-titre', false], ['t', 'Titre', false]],
  news: [['k', 'Sur-titre', false], ['t', 'Titre', false]], shop: [['t', 'Titre', false], ['b', 'Texte', true]],
  image: [['cap', 'Légende', false]], video: [['t', 'Titre', false], ['cap', 'Texte', true]], button: [['l', 'Texte du bouton', false], ['note', 'Sous le bouton', false]],
  map: [['t', 'Titre', false], ['addr', 'Adresse', false]], contact: [['k', 'Sur-titre', false], ['t', 'Titre', false]],
  gallery: [['t', 'Titre', false]], docs: [['t', 'Titre', false]], faq: [['t', 'Titre', false]],
};
export async function renderPhoneEditor(root: HTMLElement, ctx: Ctx, st: SiteState, pageId?: string): Promise<void> {
  const c = await loadCore(ctx, st, pageId, () => draw());
  if (c.page.startsWith('team-') || pageOf(c)?.kind === 'legal') c.page = c.doc.pages[0]!.id;
  mount(root, html`<section class="screen se-host" data-nav-title="Le site au téléphone"><a class="back" hidden href="#/club/site">‹ Site du club</a>
    <div class="se-mshell"><div class="se-mtop"><a class="btn btn-ghost" href="#/club/site" id="se-mquit">‹ Quitter</a>
      <button type="button" class="se-sel-page" id="se-mpage" data-guide="site-mpage"><span></span> ▾</button>
      <button type="button" class="btn btn-primary" id="se-mpub" data-guide="site-publish"></button></div>
      <div class="se-mbar">${ico('phone')}<span>Ici : textes, photos, ordre des blocs. Le reste sur ordinateur.</span></div>
      <div class="se-msite" id="se-msite"></div></div></section>`);
  const site = $(root, '#se-msite')!;
  function draw(): void {
    const p = pageOf(c)!;
    $(root, '#se-mpage span')!.textContent = p.title;
    const pub = $<HTMLButtonElement>(root, '#se-mpub')!;
    pub.textContent = c.saving ? 'Enregistrement…' : online(c) ? 'Publier' : 'Mettre en ligne';
    pub.disabled = c.saving || (online(c) && !notes(c).length);
    const scroll = site.scrollTop;
    mount(site, renderSite(view(c, 'view'), { kind: 'page', page: p }));
    site.scrollTop = scroll;
    hydrateMedia(site, id => mediaUrl(id, { club: c.clubId }), () => Promise.resolve(null));
    // Chaque bloc : ↑ ↓ (choisi), ✎ (texte), 📷 (photo), « vivant » (rien à écrire).
    const blocks = $$<HTMLElement>(site, '.sw-main > [data-bid]');
    blocks.forEach((el, i) => {
      const f = findBlock(c, el.dataset.bid!);
      if (!f) return;
      const b = f.block, sel = c.sel === b.id;
      if (sel) el.classList.add('se-mblk-sel');
      const pen = TEXT_FIELDS[b.type] && (!isLive(b.type) || ['trial', 'follow', 'shop'].includes(b.type));
      const cam = PHOTO_TYPES.has(b.type) && (b.type !== 'text' || b.variant === 'image' || b.o.bg === 'image') && b.variant !== 'centre';
      el.insertAdjacentHTML('beforeend', html`<div class="se-mtools">${sel ? html`<button type="button" data-m="up" aria-label="Monter" ${i === 0 ? 'disabled' : ''}>${ico('up')}</button>
        <button type="button" data-m="down" aria-label="Descendre" ${i === blocks.length - 1 ? 'disabled' : ''}>${ico('down')}</button>` : ''}
        ${pen ? html`<button type="button" class="pen" data-m="text" aria-label="Modifier le texte" ${i === 0 ? html`data-guide="site-mpen"` : ''}>${ico('pen')}</button>` : ''}
        ${cam ? html`<button type="button" class="pen" data-m="photo" aria-label="Changer la photo">${ico('camera')}</button>` : ''}
        ${!pen && !cam && isLive(b.type) ? html`<span class="se-mtag">vivant</span>` : ''}</div>`.s);
    });
  }
  site.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const blockEl = t.closest<HTMLElement>('[data-bid]');
    const m = t.closest<HTMLElement>('[data-m]')?.dataset.m;
    if (t.closest('a, button')) e.preventDefault();
    if (!blockEl) return;
    const id = blockEl.dataset.bid!;
    if (m === 'up' || m === 'down') { move(id, m === 'up' ? -1 : 1); return; }
    if (m === 'text') { editText(id); return; }
    if (m === 'photo') { void (async () => { const f = findBlock(c, id); const pid = await pickPhoto(c.clubId, 'Changer la photo', typeof f?.block.c.img === 'string' ? f.block.c.img : null);
      if (pid) change(c, d => { const b = d.pages.flatMap(p => p.blocks).find(x => x.id === id)!; b.c.img = pid; if (b.type === 'text' && b.variant === 'banniere') b.o.bg = 'image'; }); })(); return; }
    c.sel = c.sel === id ? null : id; draw();
  });
  function move(id: string, dir: number): void {
    const f = findBlock(c, id);
    if (!f) return;
    const j = f.i + dir;
    if (j < 0 || j >= f.page.blocks.length) return;
    change(c, d => { const p = d.pages.find(x => x.id === f.page.id)!; [p.blocks[f.i], p.blocks[j]] = [p.blocks[j]!, p.blocks[f.i]!]; });
  }
  function editText(id: string): void {
    const f = findBlock(c, id);
    if (!f) return;
    const fields = TEXT_FIELDS[f.block.type] ?? [];
    openSheet('Modifier le texte', html`<form class="form" id="se-mt" novalidate>
      ${fields.map(([k, label, long]) => long ? html`<label class="field"><span>${label}</span><textarea name="${k}" rows="5" maxlength="2000">${String(f.block.c[k] ?? '')}</textarea></label>`
        : html`<label class="field"><span>${label}</span><input name="${k}" maxlength="${k === 'addr' ? 160 : 120}" value="${String(f.block.c[k] ?? '')}"></label>`)}
      <div class="row-2"><button type="button" class="btn btn-ghost" data-close>Annuler</button><button type="submit" class="btn btn-primary">OK</button></div>
      <p class="hint">Gras, liens et couleurs : sur ordinateur.</p></form>`, (sheet, close) => {
      const form = $<HTMLFormElement>(sheet, '#se-mt')!;
      $(form, '[data-close]')!.addEventListener('click', close);
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const d0 = new FormData(form);
        change(c, d => { const b = d.pages.flatMap(p => p.blocks).find(x => x.id === id)!; for (const [k] of fields) b.c[k] = String(d0.get(k) ?? '').trim(); });
        close();
      });
    });
  }
  $(root, '#se-mpage')!.addEventListener('click', () => {
    const pages = c.doc.pages.filter(p => p.kind !== 'legal');
    openSheet('Les pages', html`<ul class="se-pagelist">${pages.map(p => html`<li><button type="button" data-goto="${p.id}" aria-pressed="${String(p.id === c.page)}"><span>${p.title}</span><small>${p.slug ? `/${p.slug}` : 'accueil'}</small></button></li>`)}</ul>
      <button type="button" class="btn btn-ghost" id="se-order" data-guide="site-order">Ordre des blocs · ${pageOf(c)!.title}</button>
      <p class="hint">Ajouter, retirer ou ranger les pages et le menu : sur ordinateur.</p>`, (sheet, close) => {
      for (const b of $$<HTMLButtonElement>(sheet, '[data-goto]')) b.addEventListener('click', () => { c.page = b.dataset.goto!; c.sel = null; close(); draw(); site.scrollTop = 0; });
      $(sheet, '#se-order')!.addEventListener('click', () => { close(); openOrder(); });
    });
  });
  function openOrder(): void {
    const p = pageOf(c)!;
    const list = () => html`<ul class="se-mlist">${p.blocks.map((b, i) => {
      const live = isLive(b.type);
      const sub = live ? liveSummary(c, b) : b.type === 'text' ? plainText(String(b.c.t ?? ''))
        : b.type === 'figures' ? ((b.c.items ?? []) as { n?: string }[]).map(i => i.n).join(' · ') : plainText(String(b.c.t ?? b.c.l ?? '')) || BLOCKS[b.type].label;
      return html`<li class="${live ? 'se-lv' : ''}"><span class="se-ti">${ico(BLOCKS[b.type].icon)}</span>
        <span><strong>${BLOCKS[b.type].label}</strong><small>${live ? html`<span class="se-live-dot">vivant</span> · ` : ''}${sub.slice(0, 40)}</small></span>
        <span class="se-ud"><button type="button" data-o="${i}" data-d="-1" aria-label="Monter" ${i === 0 ? 'disabled' : ''}>${ico('up')}</button>
        <button type="button" data-o="${i}" data-d="1" aria-label="Descendre" ${i === p.blocks.length - 1 ? 'disabled' : ''}>${ico('down')}</button></span></li>`;
    })}</ul><p class="hint">Ajouter ou supprimer un bloc : sur ordinateur.</p>`;
    openSheet(`Ordre des blocs · ${p.title}`, list(), (sheet) => {
      sheet.addEventListener('click', (e) => {
        const b = (e.target as HTMLElement).closest<HTMLElement>('[data-o]');
        if (!b) return;
        const i = Number(b.dataset.o), j = i + Number(b.dataset.d);
        change(c, d => { const pg = d.pages.find(x => x.id === p.id)!; [pg.blocks[i], pg.blocks[j]] = [pg.blocks[j]!, pg.blocks[i]!]; });
        const fresh = pageOf(c)!;
        p.blocks = fresh.blocks;
        mount(sheet.querySelector('.sheet-body')!, list());
        sheet.querySelectorAll('.se-mlist li')[j]?.classList.add('moving');
      });
    });
  }
  $(root, '#se-mpub')!.addEventListener('click', () => void publish(c, true));
  $(root, '#se-mquit')!.addEventListener('click', (e) => { e.preventDefault(); void flush(c).then(() => { location.hash = '#/club/site'; }); });
  window.addEventListener('hashchange', () => void flush(c), { once: true });
  draw();
}

/** La page d'accueil du site en petit (vignettes de l'assistant, aperçus). */
export function homePreview(v: SiteView, max = 99): Html {
  const home = v.doc.pages[0]!;
  return renderSite(v, { kind: 'page', page: { ...home, blocks: home.blocks.slice(0, max) } }, { foot: max >= home.blocks.length });
}
export { artCourt, legalBlocks, pageBlocks, teamBlocks };
